/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 *
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 *
 * Software credits:
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 *
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/* eslint-disable no-plusplus */
/*
 * Copyright (c) 2020 AXA Group Operations Spain S.A.
 *
 * Licensed under the AXA Group Operations Spain S.A. License (the "License");
 * you may not use this file except in compliance with the License.
 * A copy of the License can be found in the LICENSE.TXT file distributed
 * together with this file.
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * ### Flows graph
 * ![header](../images/frontend/dashboard/DialogCharts.png)
 * #### External libraries
 * [Cytoscape](https://js.cytoscape.org/) For graph visualization
 *  @function
 * @memberOf DashboardPage
 * @name FlowsGraph
 */

import React, { Suspense, Component, useEffect, useState } from 'react';
import DSCard from '@design-system/card/DSCard';
import { Button } from 'antd';

import Cytoscape from 'cytoscape';
import popper from 'cytoscape-popper';
import klay from 'cytoscape-klay';

import { useDispatch, useSelector } from 'react-redux';

import dashboardActions from '@store/actions/dashboard';
import { ReloadOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { SIZES } from '@constants';

import deepClone from '../../../../../../commons/utils/deepClone';
import TopBarLegends from './TopBarLegends';
import TooltipNode from '../modules/TooltipNode';
import FilterNodesDropdown from './FilterNodesDropdown';

import styleConfig from '../style/container/styleConfig';

import layoutConfig from '../modules/layoutConfig';
import dialogGraphStyles from '../style/container/dialogGraphStyles';
import dialogGraphEvents from './dialogGraphEvents';
import ReleasesDropdown from './ReleasesDropdown';
import resetStatus from '../modules/dialogGraphEvents/resetStatus';
import { useGetReleasesSummary } from '../../hooks/useGetReleasesSummary';
import { useGetDialogGraph } from '../../hooks/useGetDialogGraph';

const CytoscapeComponent = React.lazy(() => import('react-cytoscapejs'));

const CytoscapeInitialization = () => {
  Cytoscape.use(popper); // popper Tooltips
  Cytoscape.use(klay); // sorting algo klay.
  Cytoscape.warnings(false);
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <div>_</div>;
    }

    return this.props.children;
  }
}

const FlowsGraph = () => {
  CytoscapeInitialization(); // only once
  const dispatch = useDispatch();

  const [cyState, setcyState] = useState(undefined);
  const [zoomCy, setZoomCy] = useState(1);
  const [zoomClass, setZoomClass] = useState('fade-out');

  const [hoverDialog, setHoverDialog] = useState(null);
  const [graphElements, setGraphElements] = useState([]);

  const filterDropdown = useSelector(state => state.dashboard.dialogGraphs.filterDropdown);
  const graphPositionsUpdated = useSelector(state => state.dashboard.dialogGraphs.graphPositionsUpdated);
  const { summary, current, loading: loadingReleases } = useGetReleasesSummary();
  const { dialogGraph, loadingDialog } = useGetDialogGraph({ summary, loadingReleases });

  const setZoomAndFade = value => {
    setZoomCy(value);
    setZoomClass('fade');
    setTimeout(() => {
      setZoomClass('fade-out');
    }, 0);
  };

  useEffect(() => {
    dispatch(dashboardActions.setGraphPositionsUpdated(false));
  }, []);

  useEffect(() => {
    if (!loadingDialog && dialogGraph && dialogGraph.cytoScapeComplete) {
      setGraphElements(deepClone(dialogGraph.cytoScapeComplete));
    }
  }, [dialogGraph?.cytoScapeComplete]);

  console.log('FlowsGraph > graphElements -> ', graphElements);

  return (
    <>
      <ErrorBoundary>
        <DSCard size={SIZES.xlarge} titleCard="Dialog chart" hasTooltip={false}>
          {!graphElements.length && loadingDialog ? (
            <div className="loader large" />
          ) : (
            <>
              <div id="dialogGraphTopBar">
                <TopBarLegends />
                <ReleasesDropdown current={current} releases={summary} />
                <FilterNodesDropdown />
              </div>
              {graphElements.length > 0 && (
                <>
                  <div id="resetPositionsDialogGraph">
                    <Button
                      icon={<ReloadOutlined style={{ display: 'inline-flex' }} />}
                      onClick={() => resetStatus(cyState)}
                    >
                      Center View
                    </Button>
                  </div>
                  <div
                    id="zoomText"
                    className={zoomClass}
                    style={{
                      position: 'absolute',
                      zIndex: '55',
                      marginTop: '4em',
                      opacity: '0',
                    }}
                  >
                    <Text
                      style={{
                        padding: '0.5em',
                        borderRadius: '0.5em',
                      }}
                    >
                      Zoom {(zoomCy * 100).toFixed(0)} %
                    </Text>
                  </div>

                  <div id="dialogGraphContainer">
                    <Suspense fallback={<div>Loading...</div>}>
                      <CytoscapeComponent
                        elements={graphElements}
                        style={{ width: '100%', height: '600px' }}
                        stylesheet={styleConfig(filterDropdown, dialogGraph)}
                        userZoomingEnabled
                        minZoom={0.15} // far
                        maxZoom={2} // 15 default
                        wheelSensitivity={0.1}
                        motionBlur
                        boxSelectionEnabled={false}
                        layout={layoutConfig}
                        cy={cy => {
                          setcyState(cy);
                          dialogGraphEvents(cy, dispatch, graphPositionsUpdated, setHoverDialog, setZoomAndFade);
                        }}
                      />
                    </Suspense>
                  </div>
                  <div id="dialogMainLabelsContainers">
                    <div id="dialogLabelsContainers" />

                    <div id="tooltipNode" style={{ display: 'none', zIndex: '56' }}>
                      <TooltipNode currentNode={hoverDialog} />
                    </div>
                    <div id="startLabel">
                      <div className="Popperlabel">Start</div>
                    </div>
                    <div id="goalLabel">
                      <div className="Popperlabel">Goal</div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </DSCard>
      </ErrorBoundary>
      <style jsx>{dialogGraphStyles}</style>
    </>
  );
};

export default FlowsGraph;
